import React from "react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { axiosInstance } from "@/pages/auth/auth.context";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { BackLink } from "@/components/brand/BackLink";

const BatchSchema = z.object({
  startingCheckNumber: z.number().min(1, "Must be at least 1"),
  includeEmployees: z.boolean(),
  includePartners: z.boolean(),
  notes: z.string().max(500, "Notes must be 500 characters or less"),
});

type BatchFormValues = z.infer<typeof BatchSchema>;

const CreateBatchPage: React.FC = () => {
  const { id: eventId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const form = useForm<BatchFormValues>({
    resolver: zodResolver(BatchSchema),
    defaultValues: {
      includeEmployees: true,
      includePartners: true,
      notes: "",
    },
  });

  const { mutate, isPending, error } = useMutation({
    mutationFn: async (values: BatchFormValues) => {
      const { data } = await axiosInstance.post("/check-batches", {
        eventId,
        ...values,
      });
      return data;
    },
    onSuccess: async (batch) => {
      try {
        await axiosInstance.post(`/check-batches/${batch.id}/generate`);
      } catch (err) {
        let message = "There was an issue generating payments.";
        if (
          err &&
          typeof err === "object" &&
          "response" in err &&
          err.response?.data?.message
        ) {
          message = err.response.data.message;
        }
        toast({
          title: "Batch Generation Error",
          description: message,
          variant: "destructive",
        });
      }
      navigate(`/admin/check-batches/${batch.id}`);
    },
  });

  function onSubmit(values: BatchFormValues) {
    mutate(values);
  }

  return (
    <div className="py-10 px-4">
      <div>
        <BackLink to={`/admin/events/${eventId}`}>Back to Event</BackLink>
      </div>
      <div className="container max-w-md mx-auto">
        <h1 className="text-3xl font-bold text-foreground mb-8">
          Create Check Batch
        </h1>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="startingCheckNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Starting Check Number</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.valueAsNumber)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex gap-4">
              <FormField
                control={form.control}
                name="includeEmployees"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel>Include Employees</FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="includePartners"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel>Include Partners</FormLabel>
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      className="textarea textarea-bordered w-full"
                      rows={3}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {error && <div className="text-destructive">{error.message}</div>}
            <Button type="submit" className="w-full" disabled={isPending}>
              {isPending ? "Creating..." : "Create Batch"}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default CreateBatchPage;
